/*
 Lightning Module Initializer

 module auto init
 just add data-module to an HTML elem
 that will match a file in "components" folder and it will work

 <div data-module="test">TEST COMPONENT</div>
 */

/**
 * @description CreateModule.
 *
 * @param {Function} ModuleFactory - The module function.
 * @param {HTMLElement} domModule - The DOM wich containing the module.
 * @returns {object} Module object.
 */
const createModule = (ModuleFactory, domModule) => {
    const modulePattern = new ModuleFactory(domModule);
    modulePattern.listeners = modulePattern.listeners || [];
    return Object.create(modulePattern);
};

/**
 * @description Parse Module path.
 *
 * @param {string} moduleName - Module name.
 * @returns {string} - Full path.
 */
const parsePath = moduleName => {
    const splitedModuleName = moduleName.split('/');
    if(splitedModuleName.length > 1) {
        const pathModule = splitedModuleName.reduce((str, current) => `${str}${current}/`, '');
        return `${pathModule}${splitedModuleName[splitedModuleName.length - 1]}`;
    }

    return `${moduleName}/${moduleName}`;
};

/**
 * @typedef {object} Module
 * @property {Function} ready
 * @property {Function} unmount
 * @property {Array<[Node,string,Function]>} listeners
 *
 */
const lightModule = (() => {
    const config = {childList: true, subtree: true},
        MODULE_ATTRIBUTE = 'data-module';
    let modulesMounted = [];

    /**
     * @description ParseNodes.
     *
     * @param {HTMLElement} item - DOM node to parse.
     * @param {Function} callbackFunction - The callback function.
     */
    const parseNodes = (item, callbackFunction) => {
        // A new item has been added by mutation Observer

        // If item is not a DOM element return nothing
        if(!item.tagName || !MODULE_ATTRIBUTE) {
            return;
        }

        if(item.hasAttribute(MODULE_ATTRIBUTE)) {
            callbackFunction(item);
        }

        // Parse all nodes in DOM (ex: document.body)
        if(item.querySelector(`[${MODULE_ATTRIBUTE}]`)) {
            const domNodesList = [...item.querySelectorAll(`[${MODULE_ATTRIBUTE}]`)];
            domNodesList.forEach(domElement => {
                callbackFunction(domElement);
            });
        }
    };

    const getModuleName = domModule => {
        const moduleAttribute = domModule.getAttribute(MODULE_ATTRIBUTE);

        if(!moduleAttribute) {
            throw new Error(`WEB MODULE : Missing module ${domModule.toString()}`);
        } else {
            return moduleAttribute;
        }
    };

    /**
     * @description LoopModules.
     *
     * @param {string} type - Type.
     * @param {HTMLElement} domModule - DOMModule.
     * @param {Function} callbackFunction - Callback.
     */
    const loopModules = (type, domModule, callbackFunction) => {
        getModuleName(domModule)
            .trim()
            .split(' ')
            .forEach(moduleName => {
                if(!moduleName) {
                    return;
                }

                try {
                    callbackFunction(moduleName);
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('WEB MODULE: Callback not found', moduleName, type, error);
                }
            });
    };

    /**
     * @description AddModule.
     *
     * @param {HTMLElement} domModule - DOMModule.
     */
    const addModule = domModule => {
        const exist = modulesMounted.filter(modulePattern => domModule.isSameNode(modulePattern.elem));

        // istanbul ignore else
        if(!exist.length) {
            loopModules('addModule', domModule, moduleName => {
                const pathModule = parsePath(moduleName);
                // eslint-disable-next-line no-inline-comments, capitalized-comments, function-paren-newline
                import(/* webpackMode: "lazy", webpackExclude: /\.spec\.(js|ts)$/, webpackChunkName: "[request]", webpackInclude: /\.(js|ts)$/ */
                    `Components/${pathModule}`)
                    .then(importModule => {
                        if(!importModule) {
                            return;
                        }
                        const modulePattern = createModule(importModule.default, domModule);
                        modulesMounted.push({
                            module: modulePattern,
                            elem: domModule
                        });
                    })
                    // eslint-disable-next-line no-console
                    .catch(error => console.log(error));
            });
        }
    };

    /**
     * @description RemoveModule.
     *
     * @param {HTMLElement} domModule - DOMModule.
     */
    const removeModule = domModule => {
        loopModules('removeModule', domModule, () => {
            modulesMounted = modulesMounted.filter(modulePattern => {
                const exist = domModule.isSameNode(modulePattern.elem);
                return !exist;
            });
        });
    };

    /**
     * @description MutationObserver.
     */
    const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            // Added Nodes
            [...mutation.addedNodes]
                .forEach(item => {
                    parseNodes(item, addModule);
                });
            // Removed Nodes
            [...mutation.removedNodes]
                .forEach(item => {
                    parseNodes(item, removeModule);
                });
        });
    });

    const init = (domTarget = document.body) => {
        // Listen Dom Changes
        observer.observe(domTarget, config);

        // Initialize Modules in body on dom ready
        parseNodes(document.body, addModule);
    };

    return {init};
})();

export default lightModule;
